import { useState } from "react";
import { toast } from "react-toastify";
import { useSimpleRequest } from "hooks/useRequest";
import type { PreApplicationOnboarding } from "features/ecommerce/types";
import { PreApplication } from "ui/forms/ECommerce";
import { postOnboardingRegister } from "features/ecommerce/api";

export const usePreOnboarding = () => {
    const [isRegistrationComplete, setRegistrationComplete] = useState(false);

    const { BusinessActivityOption } = PreApplication;

    const request = useSimpleRequest();

    const register = (preApplication: PreApplicationOnboarding) => (
        request(() => postOnboardingRegister(preApplication), {
            onSuccess: () => setRegistrationComplete(true),
            onError: ({ message }) => toast.error(message)
        })
    );

    const bootstrapData: PreApplication.PreApplicationBootstrapData = {
        businessActivities: [
            BusinessActivityOption.from('retail', 'Retail (Clothing, Electronics, Furniture)')
                .withProductsAndServices([
                    BusinessActivityOption.make(`Apparel (Men's, Women's, Kids)`),
                    BusinessActivityOption.make(`Electronics (Phones, Laptops, Gadgets)`),
                    BusinessActivityOption.make(`Footwear`),
                    BusinessActivityOption.make(`Accessories (Jewelry, Watches, Bags)`),
                    BusinessActivityOption.make(`Consumer Electronics (Smartphones, Laptops, Accessories)`),
                    BusinessActivityOption.make(`Home Appliances (Vacuum Cleaners, Refrigerators)`),
                    BusinessActivityOption.make(`Furniture (Sofas, Tables, Beds)`),
                    BusinessActivityOption.make(`Home Decor (Lighting, Rugs, Wall Art)`),
                    BusinessActivityOption.make(`DIY & Tools`),
                    BusinessActivityOption.make(`Office Supplies & Stationery`),
                    BusinessActivityOption.make(`Pet Supplies & Accessories`)
                ]),

            BusinessActivityOption.from('professionalServices', 'Professional Services (Legal, Consulting)')
                .withProductsAndServices([
                    BusinessActivityOption.make(`Legal Advisory`),
                    BusinessActivityOption.make(`Accounting & Bookkeeping`),
                    BusinessActivityOption.make(`Business Consulting`),
                    BusinessActivityOption.make(`Marketing & Branding Services`),
                    BusinessActivityOption.make(`Financial Planning (Non-investment)`),
                    BusinessActivityOption.make(`HR & Recruitment Services`),
                    BusinessActivityOption.make(`IT Consulting`),
                    BusinessActivityOption.make(`Graphic Design & UI/UX`),
                    BusinessActivityOption.make(`Translation Services`),
                    BusinessActivityOption.make(`Career Coaching`)
                ]),

            BusinessActivityOption.from('saas', 'SaaS (Non-Financial Software)')
                .withProductsAndServices([
                    BusinessActivityOption.make(`Project Management Software`),
                    BusinessActivityOption.make(`CRM & Marketing Automation`),
                    BusinessActivityOption.make(`Productivity Tools (Calendars, Note-Taking Apps)`),
                    BusinessActivityOption.make(`HR & Payroll Management Software`),
                    BusinessActivityOption.make(`E-Learning & Training Platforms`),
                    BusinessActivityOption.make(`Customer Support & Chatbots`),
                    BusinessActivityOption.make(`ERP Systems`),
                    BusinessActivityOption.make(`Document Management Software`),
                    BusinessActivityOption.make(`Cloud Storage & Collaboration Tools`)
                ]),

            BusinessActivityOption.from('digitalContent', 'Digital Content (E-books, Streaming)')
                .withProductsAndServices([
                    BusinessActivityOption.make(`E-books & Audiobooks`),
                    BusinessActivityOption.make(`Online Courses & Webinars`),
                    BusinessActivityOption.make(`Stock Photos & Videos`),
                    BusinessActivityOption.make(`Streaming Services (Music, Podcasts, Video)`),
                    BusinessActivityOption.make(`Design Templates (Canva, Photoshop, Presentation Slides)`),
                    BusinessActivityOption.make(`Gaming & Virtual Goods (Non-Gambling)`),
                    BusinessActivityOption.make(`Mobile Apps & Software Licenses`),
                    BusinessActivityOption.make(`Virtual Fitness & Wellness Programs`)
                ]),

            BusinessActivityOption.from('healthProducts', 'Supplements, Health Products')
                .withProductsAndServices([
                    BusinessActivityOption.make(`Vitamins & Dietary Supplements`),
                    BusinessActivityOption.make(`Herbal Remedies`),
                    BusinessActivityOption.make(`Sports Nutrition (Protein Powders, Energy Drinks)`),
                    BusinessActivityOption.make(`Beauty & Skincare (Cosmetics, Anti-Aging Creams)`),
                    BusinessActivityOption.make(`Organic & Natural Products`),
                    BusinessActivityOption.make(`Weight Loss Products`),
                    BusinessActivityOption.make(`Sexual Wellness Products`),
                    BusinessActivityOption.make(`Essential Oils & Aromatherapy`)
                ]),

            BusinessActivityOption.from('subscriptionBased', 'Subscription-Based Services')
                .withProductsAndServices([
                    BusinessActivityOption.make(`Subscription Boxes (Food, Beauty, Tech Gadgets)`),
                    BusinessActivityOption.make(`Streaming & Digital Content Memberships`),
                    BusinessActivityOption.make(`Online Coaching & Masterclasses`),
                    BusinessActivityOption.make(`Software-as-a-Service (SaaS) with Recurring Billing`),
                    BusinessActivityOption.make(`Cloud Storage & Hosting Services`),
                    BusinessActivityOption.make(`Membership-Based Communities`),
                    BusinessActivityOption.make(`Print & Digital Magazine Subscriptions`)
                ]),

            BusinessActivityOption.from('ticketing', 'Ticketing Services')
                .withProductsAndServices([
                    BusinessActivityOption.make(`Concert & Festival Tickets`),
                    BusinessActivityOption.make(`Sports Event Tickets`),
                    BusinessActivityOption.make(`Theater & Entertainment Tickets`),
                    BusinessActivityOption.make(`Travel & Airline Ticketing`),
                    BusinessActivityOption.make(`Hotel & Vacation Package Bookings`),
                    BusinessActivityOption.make(`Museum & Attraction Passes`)
                ]),

            BusinessActivityOption.from('travelAgencies', 'Travel Agencies')
                .withProductsAndServices([
                    BusinessActivityOption.make(`Flight Bookings`),
                    BusinessActivityOption.make(`Hotel Reservations`),
                    BusinessActivityOption.make(`Tour Packages & Experiences`),
                    BusinessActivityOption.make(`Car Rentals`),
                    BusinessActivityOption.make(`Travel Insurance`),
                    BusinessActivityOption.make(`Visa & Immigration Assistance`),
                    BusinessActivityOption.make(`Vacation Rentals (Airbnb-style)`),
                    BusinessActivityOption.make(`Cruise Packages`)
                ]),

            BusinessActivityOption.from('b2b', 'Business-to-Business (B2B)')
                .withProductsAndServices([
                    BusinessActivityOption.make(`Wholesale Goods & Dropshipping`),
                    BusinessActivityOption.make(`SaaS for Enterprise (Business Productivity Tools)`),
                    BusinessActivityOption.make(`Supply Chain & Procurement Services`),
                    BusinessActivityOption.make(`Cloud Computing Services`),
                    BusinessActivityOption.make(`Business Loans & Financing (Non-Banking)`),
                    BusinessActivityOption.make(`Corporate Training & Compliance`),
                    BusinessActivityOption.make(`Digital Marketing Services (SEO, PPC, Social Media)`),
                    BusinessActivityOption.make(`Office Equipment & Bulk Supplies`)
                ]),

            BusinessActivityOption.from('gambling', 'Gambling/Betting')
                .withProductsAndServices([
                    BusinessActivityOption.make(`Online Casino & Slots`),
                    BusinessActivityOption.make(`Sports Betting`),
                    BusinessActivityOption.make(`Poker & Card Games`),
                    BusinessActivityOption.make(`Fantasy Sports`),
                    BusinessActivityOption.make(`Lottery & Sweepstakes`),
                    BusinessActivityOption.make(`Esports Betting`),
                    BusinessActivityOption.make(`Bingo & Raffles`)
                ]),

            BusinessActivityOption.from('forexCrypto', 'Forex & Crypto Exchanges')
                .withProductsAndServices([
                    BusinessActivityOption.make(`Crypto Trading Platforms`),
                    BusinessActivityOption.make(`Forex Brokerage Services`),
                    BusinessActivityOption.make(`Crypto Wallets & Custody Solutions`),
                    BusinessActivityOption.make(`NFT Marketplaces`),
                    BusinessActivityOption.make(`Decentralized Finance (DeFi) Platforms`),
                    BusinessActivityOption.make(`Initial Coin Offerings (ICOs)`),
                    BusinessActivityOption.make(`Staking & Yield Farming Services`)
                ])
        ]
    };

    return {
        isRegistrationComplete,
        register,
        bootstrapData
    };
};

export default usePreOnboarding;
